
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useCallback, useState } from 'react';
import { 
  type SubmitReferentsFormData, 
  type RelationType,
  createQuickSubmitReferentsSchema
} from '@/types/referents';
import { validateAllReferentEmails } from '@/utils/emailValidation';
import { ValidationService } from '@/services/ValidationService';
import { ErrorHandler } from '@/services/ErrorHandler';

export const useOptimizedSubmitReferentsForm = (candidateEmail: string, referentsCount: number = 3) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});
  
  // Instance du service de validation
  const validationService = useMemo(() => new ValidationService(), []);

  // Utiliser le schéma rapide pour la validation en temps réel
  const quickValidationSchema = useMemo(() => createQuickSubmitReferentsSchema(), []);
  


  const form = useForm<SubmitReferentsFormData>({
    resolver: zodResolver(quickValidationSchema), // Utiliser la validation rapide par défaut
    mode: 'onChange',
    defaultValues: {
      referents: Array.from({ length: referentsCount }, () => ({
        name: '',
        email: '',
        position: '',
        relation_type: 'manager' as RelationType,
      })),
    },
  });

  // Validation finale avant soumission (avec validations coûteuses)
  const validateForSubmission = useCallback(async (data: SubmitReferentsFormData) => {
    setIsValidating(true);
    
    try {
      // Utiliser le service de validation unifié
      await validationService.validate('submitReferents', data);
      
      // Filtrer les référents avec email non vide et typer correctement
      const referentsWithEmails = data.referents
        .filter((referent): referent is typeof referent & { email: string } => 
          typeof referent.email === 'string' && referent.email.trim() !== ''
        );
      
      // Validation supplémentaire des emails
      const { isValid, errors } = validateAllReferentEmails(referentsWithEmails, candidateEmail);
      
      if (!isValid) {
        setValidationErrors(errors);
        return { isValid: false, errors };
      }
      
      setValidationErrors({});
      return { isValid: true, errors: {} };
    } catch (error: unknown) {
      console.error('Erreur de validation:', error);
      const handledError = ErrorHandler.handle(error as Error);
      return { 
        isValid: false, 
        errors: { general: handledError.message } 
      };
    } finally {
      setIsValidating(false);
    }
  }, [validationService, candidateEmail]);

  // Mémoriser la fonction pour éviter les re-créations
  const updateReferentsCount = useCallback((count: number) => {
    const currentReferents = form.getValues('referents');
    const newReferents = Array.from({ length: count }, (_, index) => {
      // Garder les données existantes si elles existent
      return currentReferents[index] || {
        name: '',
        email: '',
        position: '',
        relation_type: 'manager' as RelationType,
      };
    });
    
    form.setValue('referents', newReferents);
  }, [form]);

  return {
    form,
    updateReferentsCount,
    validateForSubmission,
    isValidating,
    validationErrors,
  };
};
